import React, { useState, useEffect } from 'react';
import { Modal, Form, Row, Col, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaSave, FaTimes, FaMoneyBillWave, FaClock, FaGift, FaCalendarDay, FaCalculator, FaLandmark, FaWallet, FaBalanceScale } from 'react-icons/fa';
import planillaEmpleadosApi from '../../../services/api/planillaEmpleadosApi';

const PlanillaModal = ({ show, onHide, onSaved, data, anio, mes, proyectoId }) => {
    const [saving, setSaving] = useState(false);
    const [showExtras, setShowExtras] = useState(false);

    // Initial state setup based on the fields we want to track right now
    const [form, setForm] = useState({
        dias_trabajados: 30,
        sueldo_valor_dia: 0,
        remuneracion: 0,
        asignacion_familiar: 0,
        hhee_25_cantidad: 0,
        hhee_25_valor: 0,
        hhee_25_monto: 0,
        hhee_33_cantidad: 0,
        hhee_33_valor: 0,
        hhee_33_monto: 0,
        bono_fijo: 0,
        bono_fijo_observacion: '',
        movilidad_monto: 0,
        feriado_trabajado_dias: 0,
        feriado_trabajado_valor: 0,
        feriado_trabajado_monto: 0,
        descanso_vendido_dias: 0,
        descanso_vendido_monto: 0,
        sueldo_bruto: 0,
        sistema_privado_pension_id: null,
        porc_aporte_afp_onp: 0,
        desc_aporte_afp_onp: 0,
        porc_prima_seguro_comision: 0,
        desc_prima_seguro_comision: 0,
        desc_quinta_categoria: 0,
        desc_adelanto: 0,
        sueldo_neto: 0,
        deposito_sueldo: 0,
        deposito_bono_descanso: 0,
        deposito_movilidad: 0,
        deposito_total: 0,
        // Mantener track de variables base del empleado
        sueldo_congelado: 0
    });

    // Populate data when modal opens
    useEffect(() => {
        if (!show || !data) return;
        const raw = data.raw_data || {};
        
        const isNew = data.estado === 'pendiente';

        // Set initial values
        setForm(prev => ({
            ...prev,
            sueldo_congelado: isNew ? parseFloat(raw.sueldo || 0) : parseFloat(raw.sueldo_congelado || raw.sueldo || 0),
            dias_trabajados: isNew ? 30 : parseInt(raw.dias_trabajados || 30),
            sueldo_valor_dia: parseFloat(raw.sueldo_valor_dia || 0),
            asignacion_familiar: parseFloat(raw.asignacion_familiar_valor || raw.asignacion_familiar || 0),
            
            hhee_25_cantidad: isNew ? 0 : parseFloat(raw.hhee_25_cantidad || 0),
            hhee_25_valor: parseFloat(raw.sueldo_valor_hhee_25 || raw.hhee_25_valor || 0),
            
            hhee_33_cantidad: isNew ? 0 : parseFloat(raw.hhee_33_cantidad || 0),
            hhee_33_valor: parseFloat(raw.sueldo_valor_hhee_33 || raw.hhee_33_valor || 0),
            
            bono_fijo: parseFloat(raw.bonificacion_fija_valor || raw.bono_fijo || 0),
            bono_fijo_observacion: raw.bonificacion_observacion || raw.bono_fijo_observacion || '',
            movilidad_monto: isNew ? 0 : parseFloat(raw.movilidad_monto || 0),
            
            feriado_trabajado_dias: isNew ? 0 : parseFloat(raw.feriado_trabajado_dias || 0),
            feriado_trabajado_valor: parseFloat(raw.sueldo_valor_dia_feriado || raw.feriado_trabajado_valor || 0),
            
            descanso_vendido_dias: isNew ? 0 : parseFloat(raw.descanso_vendido_dias || 0),
            
            // AFP/ONP Porcentajes vienen del query maestro
            sistema_privado_pension_id: raw.spp_id || raw.sistema_privado_pension_id || null,
            porc_aporte_afp_onp: parseFloat(raw.porc_aporte_afp_onp || 0),
            porc_prima_seguro_comision: parseFloat(raw.porc_prima_seguro_comision || 0),
            
            desc_quinta_categoria: isNew ? 0 : parseFloat(raw.desc_quinta_categoria || 0),
            desc_adelanto: isNew ? 0 : parseFloat(raw.desc_adelanto || 0),
        }));

        // Si ya hay horas extras guardadas, abrir la sección
        if (!isNew && (parseFloat(raw.hhee_25_cantidad || 0) > 0 || parseFloat(raw.hhee_33_cantidad || 0) > 0)) {
            setShowExtras(true);
        } else {
            setShowExtras(false);
        }
    }, [show, data]);

    // Calcular reactivamente los campos dependientes
    useEffect(() => {
        if (!show || !data) return;
        const raw = data.raw_data || {};
        const isNew = data.estado === 'pendiente';

        setForm(prev => {
            // Remuneración
            let rem = prev.remuneracion;
            const originalDias = isNew ? 30 : parseInt(raw.dias_trabajados || 30);
            
            if (isNew || prev.dias_trabajados !== originalDias) {
                if (prev.dias_trabajados === 30) {
                    rem = prev.sueldo_congelado;
                } else {
                    rem = prev.dias_trabajados * prev.sueldo_valor_dia;
                }
            } else if (!isNew) {
                rem = parseFloat(raw.remuneracion || 0);
            }

            // Horas Extras Montos
            const he25 = prev.hhee_25_cantidad * prev.hhee_25_valor;
            const he33 = prev.hhee_33_cantidad * prev.hhee_33_valor;

            // Feriados y Descansos Montos
            const feriado_monto = prev.feriado_trabajado_dias * prev.feriado_trabajado_valor;
            const descanso_monto = prev.descanso_vendido_dias * prev.sueldo_valor_dia;

            // Sueldo Bruto = remuneracion + asignacion_familiar + hhee_25_monto + hhee_33_monto
            const bruto = rem + prev.asignacion_familiar + he25 + he33;

            // Descuentos
            const desc_afp_onp = (prev.porc_aporte_afp_onp * bruto) / 100;
            const desc_prima = (prev.porc_prima_seguro_comision * bruto) / 100;

            // Sueldo Neto
            const neto = bruto - desc_afp_onp - desc_prima - prev.desc_quinta_categoria - prev.desc_adelanto;

            // Depósitos
            const dep_bono_descanso = prev.bono_fijo + feriado_monto + descanso_monto;
            const dep_movilidad = parseFloat(prev.movilidad_monto) || 0;
            const dep_total = neto + dep_bono_descanso + dep_movilidad;

            return {
                ...prev,
                remuneracion: parseFloat(rem.toFixed(2)),
                hhee_25_monto: parseFloat(he25.toFixed(2)),
                hhee_33_monto: parseFloat(he33.toFixed(2)),
                feriado_trabajado_monto: parseFloat(feriado_monto.toFixed(2)),
                descanso_vendido_monto: parseFloat(descanso_monto.toFixed(2)),
                sueldo_bruto: parseFloat(bruto.toFixed(2)),
                desc_aporte_afp_onp: parseFloat(desc_afp_onp.toFixed(2)),
                desc_prima_seguro_comision: parseFloat(desc_prima.toFixed(2)),
                sueldo_neto: parseFloat(neto.toFixed(2)),
                deposito_sueldo: parseFloat(neto.toFixed(2)),
                deposito_bono_descanso: parseFloat(dep_bono_descanso.toFixed(2)),
                deposito_movilidad: parseFloat(dep_movilidad.toFixed(2)),
                deposito_total: parseFloat(dep_total.toFixed(2))
            };
        });
    }, [
        form.dias_trabajados, form.sueldo_valor_dia, form.sueldo_congelado, form.asignacion_familiar,
        form.hhee_25_cantidad, form.hhee_25_valor,
        form.hhee_33_cantidad, form.hhee_33_valor,
        form.feriado_trabajado_dias, form.feriado_trabajado_valor,
        form.descanso_vendido_dias, form.bono_fijo, form.movilidad_monto,
        form.porc_aporte_afp_onp, form.porc_prima_seguro_comision, form.desc_quinta_categoria, form.desc_adelanto,
        show, data
    ]);

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'number' ? (value === '' ? 0 : parseFloat(value)) : value
        }));
    };

    const handleSave = async () => {
        if (form.dias_trabajados < 1 || form.dias_trabajados > 30) {
            alert("Ingrese un N° Días Trabajados correcto (de 1 a 30)");
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...form,
                anio: parseInt(anio),
                mes: parseInt(mes),
                empleado_id: data.empleado_id,
                proyecto_id: proyectoId ? parseInt(proyectoId) : null
            };

            if (data.estado === 'pendiente') {
                await planillaEmpleadosApi.createPlanilla(payload);
            } else {
                await planillaEmpleadosApi.updatePlanilla(data.id, payload);
            }
            onSaved();
        } catch (error) {
            console.error("Error guardando planilla:", error);
            alert("Ocurrió un error al guardar la planilla.");
        } finally {
            setSaving(false);
        }
    };

    if (!data) return null;

    const SectionTitle = ({ title, icon }) => (
        <h6 style={{ color: '#4f46e5', fontWeight: 700, borderBottom: '2px solid #e0e7ff', paddingBottom: 8, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, marginTop: 20 }}>
            {icon} {title}
        </h6>
    );

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static" centered>
            <Modal.Header style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff', border: 'none' }}>
                <Modal.Title style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                    <FaMoneyBillWave style={{ marginRight: 10 }} />
                    {data.estado === 'pendiente' ? 'Generar' : 'Editar'} Planilla: {data.nombre_completo}
                </Modal.Title>
                <FaTimes style={{ cursor: 'pointer', fontSize: 18 }} onClick={onHide} />
            </Modal.Header>
            <Modal.Body style={{ background: '#f8fafc', padding: '24px 32px', maxHeight: '70vh', overflowY: 'auto' }}>
                
                <SectionTitle title="Remuneración" icon={<FaMoneyBillWave />} />
                <Row>
                    <Col md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>Días Trabajados</Form.Label>
                            <Form.Control type="number" name="dias_trabajados" value={form.dias_trabajados} onChange={handleChange} max={30} min={0} />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>Valor Día (S/)</Form.Label>
                            <Form.Control type="number" value={form.sueldo_valor_dia.toFixed(2)} readOnly style={{ background: '#e5e7eb' }} />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>Remuneración (S/)</Form.Label>
                            <Form.Control type="text" value={form.remuneracion.toFixed(2)} readOnly style={{ background: '#d1fae5', color: '#065f46', fontWeight: 700 }} />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>Asig. Familiar (S/)</Form.Label>
                            <Form.Control type="number" value={form.asignacion_familiar.toFixed(2)} readOnly style={{ background: '#e5e7eb' }} />
                        </Form.Group>
                    </Col>
                </Row>

                <SectionTitle title="Feriados y Descanso Vendido" icon={<FaCalendarDay />} />
                <Row>
                    <Col md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>Feriado (Días)</Form.Label>
                            <Form.Control type="number" name="feriado_trabajado_dias" value={form.feriado_trabajado_dias} onChange={handleChange} min={0} />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>Feriado Monto (S/)</Form.Label>
                            <Form.Control type="text" value={form.feriado_trabajado_monto.toFixed(2)} readOnly style={{ background: '#d1fae5', color: '#065f46', fontWeight: 700 }} />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>Descanso Vendido (Días)</Form.Label>
                            <Form.Control type="number" name="descanso_vendido_dias" value={form.descanso_vendido_dias} onChange={handleChange} min={0} />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>Descanso Monto (S/)</Form.Label>
                            <Form.Control type="text" value={form.descanso_vendido_monto.toFixed(2)} readOnly style={{ background: '#d1fae5', color: '#065f46', fontWeight: 700 }} />
                        </Form.Group>
                    </Col>
                </Row>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20 }}>
                    <SectionTitle title="Horas Extras (HHEE)" icon={<FaClock />} />
                    <Form.Check 
                        type="switch"
                        id="hhee-switch"
                        label="Registrar HHEE"
                        checked={showExtras}
                        onChange={(e) => setShowExtras(e.target.checked)}
                        style={{ fontWeight: 600, color: '#4b5563' }}
                    />
                </div>
                {showExtras && (
                    <div style={{ background: '#fff', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb', marginBottom: 16 }}>
                        <Row>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>HHEE 25% (Horas)</Form.Label>
                                    <Form.Control type="number" name="hhee_25_cantidad" value={form.hhee_25_cantidad} onChange={handleChange} min={0} step="0.5" />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>Monto 25% (S/)</Form.Label>
                                    <Form.Control type="text" value={form.hhee_25_monto.toFixed(2)} readOnly style={{ background: '#fef3c7', color: '#92400e', fontWeight: 700 }} />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>HHEE 33% (Horas)</Form.Label>
                                    <Form.Control type="number" name="hhee_33_cantidad" value={form.hhee_33_cantidad} onChange={handleChange} min={0} step="0.5" />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>Monto 33% (S/)</Form.Label>
                                    <Form.Control type="text" value={form.hhee_33_monto.toFixed(2)} readOnly style={{ background: '#fef3c7', color: '#92400e', fontWeight: 700 }} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>
                )}

                <SectionTitle title="Bonos y Movilidad" icon={<FaGift />} />
                <Row>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>Movilidad (S/)</Form.Label>
                            <Form.Control type="number" name="movilidad_monto" value={form.movilidad_monto} onChange={handleChange} min={0} />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>Bono Fijo (S/)</Form.Label>
                            <Form.Control type="number" value={form.bono_fijo.toFixed(2)} readOnly style={{ background: '#e5e7eb' }} />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>Obs. Bono</Form.Label>
                            <Form.Control type="text" value={form.bono_fijo_observacion} readOnly style={{ background: '#e5e7eb' }} />
                        </Form.Group>
                    </Col>
                </Row>

                {/* --- NUEVO BLOQUE: Bruto, Descuentos, Neto --- */}
                
                <div style={{ background: '#dbeafe', padding: '12px 16px', borderRadius: 8, marginTop: 16, border: '1px solid #bfdbfe' }}>
                    <Row className="align-items-center">
                        <Col md={8}>
                            <h5 style={{ margin: 0, color: '#1e40af', fontWeight: 700 }}><FaCalculator /> SUELDO BRUTO</h5>
                            <span style={{ fontSize: 11, color: '#3b82f6' }}>Remuneración + Asig. + HHEE + Feriados y D. Vendido</span>
                        </Col>
                        <Col md={4} className="text-end">
                            <h4 style={{ margin: 0, color: '#1d4ed8', fontWeight: 800 }}>S/ {form.sueldo_bruto.toFixed(2)}</h4>
                        </Col>
                    </Row>
                </div>

                <SectionTitle title="Descuentos (Previsionales y Otros)" icon={<FaLandmark />} />
                <Row>
                    <Col md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>Aporte AFP/ONP (%)</Form.Label>
                            <Form.Control type="number" value={form.porc_aporte_afp_onp.toFixed(2)} readOnly style={{ background: '#e5e7eb' }} />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 700, color: '#991b1b' }}>Desc. Aporte (S/)</Form.Label>
                            <Form.Control type="text" value={form.desc_aporte_afp_onp.toFixed(2)} readOnly style={{ background: '#fee2e2', color: '#991b1b', fontWeight: 700 }} />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>Seguro/Comisión (%)</Form.Label>
                            <Form.Control type="number" value={form.porc_prima_seguro_comision.toFixed(2)} readOnly style={{ background: '#e5e7eb' }} />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 700, color: '#991b1b' }}>Desc. Seguro (S/)</Form.Label>
                            <Form.Control type="text" value={form.desc_prima_seguro_comision.toFixed(2)} readOnly style={{ background: '#fee2e2', color: '#991b1b', fontWeight: 700 }} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>Renta 5ta Categoría (S/)</Form.Label>
                            <Form.Control type="number" name="desc_quinta_categoria" value={form.desc_quinta_categoria} onChange={handleChange} min={0} />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: '#4b5563' }}>Adelantos (S/)</Form.Label>
                            <Form.Control type="number" name="desc_adelanto" value={form.desc_adelanto} onChange={handleChange} min={0} />
                        </Form.Group>
                    </Col>
                </Row>

                <div style={{ background: '#f3e8ff', padding: '16px', borderRadius: 8, marginTop: 16, border: '1px solid #d8b4fe' }}>
                    <Row className="align-items-center">
                        <Col md={8}>
                            <h5 style={{ margin: 0, color: '#6b21a8', fontWeight: 700 }}><FaWallet /> SUELDO NETO</h5>
                            <span style={{ fontSize: 11, color: '#7e22ce' }}>Bruto - Descuentos</span>
                        </Col>
                        <Col md={4} className="text-end">
                            <h3 style={{ margin: 0, color: '#581c87', fontWeight: 800 }}>S/ {form.sueldo_neto.toFixed(2)}</h3>
                        </Col>
                    </Row>
                </div>

                <div style={{ background: '#ecfccb', padding: '16px', borderRadius: 8, marginTop: 16, border: '1px solid #d9f99d' }}>
                    <Row className="align-items-center mb-3">
                        <Col md={8}>
                            <h5 style={{ margin: 0, color: '#3f6212', fontWeight: 700 }}><FaBalanceScale /> DEPÓSITO TOTAL</h5>
                            <span style={{ fontSize: 11, color: '#4d7c0f' }}>Sueldo + Bono/Feriado/D.Vendido + Movilidad</span>
                        </Col>
                        <Col md={4} className="text-end">
                            <h3 style={{ margin: 0, color: '#166534', fontWeight: 800 }}>S/ {form.deposito_total.toFixed(2)}</h3>
                        </Col>
                    </Row>
                    <hr style={{ borderColor: '#bef264' }}/>
                    <Row>
                        <Col md={4}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#65a30d', textTransform: 'uppercase' }}>Depósito Sueldo</div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: '#166534' }}>S/ {form.deposito_sueldo.toFixed(2)}</div>
                        </Col>
                        <Col md={4}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#65a30d', textTransform: 'uppercase' }}>Dep. Bono/Feriado/D.Vendido</div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: '#166534' }}>S/ {form.deposito_bono_descanso.toFixed(2)}</div>
                        </Col>
                        <Col md={4}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#65a30d', textTransform: 'uppercase' }}>Depósito Movilidad</div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: '#166534' }}>S/ {form.deposito_movilidad.toFixed(2)}</div>
                        </Col>
                    </Row>
                </div>

            </Modal.Body>
            <Modal.Footer style={{ background: '#f1f5f9', borderTop: '1px solid #e2e8f0' }}>
                <button onClick={onHide} disabled={saving}
                    style={{ background: '#94a3b8', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, fontWeight: 600 }}>
                    Cancelar
                </button>
                <button onClick={handleSave} disabled={saving}
                    style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff', border: 'none', padding: '8px 24px', borderRadius: 8, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FaSave /> {saving ? 'Procesando...' : 'Guardar y Continuar'}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default PlanillaModal;
